import { getStoredAuthToken } from "@/lib/auth";
import { getStoredAdminAuthToken } from "@/lib/admin/auth";
import { apiFetch, getWebSocketHost, getWebSocketProtocol } from "@/lib/fetch";
import { WebSocketMessageData } from "@/types";
import { loadMatchingClientConfig } from "../../shared/configLoader";
import { ClientConfig } from "shared/types";

let socket: WebSocket | null = null;
let messageQueue: (string | Blob)[] = []; // Queue for messages to send
let isConnecting = false;
let currentSessionId: string | null = null;
let connectionAbortController: AbortController | null = null;

interface SocketHandlers {
  onOpen: () => void;
  onMessage: (data: WebSocketMessageData | Blob) => void;
  onClose: (event: CloseEvent) => void;
  onError: (event: Event) => void;
}

// Function to get Session ID and Room ID from your backend
export async function getSessionId(
  isAdmin: boolean,
  requestedRoomId: string | null,
  currentRoomId?: string | null
): Promise<{ sessionId: string; roomId: string } | null> {
  const apiUrl = "/xtalk";
  const { token } = isAdmin ? getStoredAdminAuthToken() : getStoredAuthToken();
  let clientConfig: ClientConfig | null = null;
  if (currentRoomId) {
    clientConfig = loadMatchingClientConfig({
      roomId: currentRoomId
    })
  }
  const roomPrefix = clientConfig?.config.roomPrefix;

  try {
    const response = await apiFetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        action: "join",
        roomId: requestedRoomId,
        roomIdPrefix: roomPrefix
      }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        `HTTP error! Status: ${response.status}, Body: ${errorText}`
      );
      throw new Error(`Failed to get session: ${response.statusText}`);
    }
    const data = await response.json();
    if (data.sessionId && data.roomId) {
      return { sessionId: data.sessionId, roomId: data.roomId };
    } else {
      console.error("Invalid session data received:", data);
      throw new Error("Invalid session data from server.");
    }
  } catch (error) {
    console.error("Error fetching session ID:", error);
    return null;
  }
}

function flushMessageQueue() {
  while (
    messageQueue.length > 0 &&
    socket &&
    socket.readyState === WebSocket.OPEN
  ) {
    const message = messageQueue.shift();
    if (message) {
      try {
        console.log(
          "Sending queued message:",
          typeof message === "string" ? JSON.parse(message).type : "Blob"
        );
        socket.send(message);
      } catch (e) {
        console.error("Error sending queued message:", e);
        messageQueue.unshift(message); // Put back to retry
        break; // Stop processing queue if send fails
      }
    }
  }
}

export function initializeWebSocket(
  isAdmin: boolean,
  sessionId: string,
  handlers: SocketHandlers
) {
  // If already connecting to the same session, don't start another connection
  if (isConnecting && currentSessionId === sessionId) {
    console.warn("Already connecting to the same session:", sessionId);
    return;
  }

  // If there's an existing connection or we're connecting to a different session
  if (socket || isConnecting) {
    console.log("Closing existing connection before starting new one");
    closeWebSocket();
  }

  // Abort any pending connection
  if (connectionAbortController) {
    connectionAbortController.abort();
  }
  connectionAbortController = new AbortController();

  // Set connecting state
  isConnecting = true;
  currentSessionId = sessionId;

  // Determine WebSocket URL (handle potential subpath like /npc)
  const wsProtocol = getWebSocketProtocol();
  const wsHost = getWebSocketHost();
  let basePath = window.location.pathname.replace(/(\/[^/]*|\/)$/, ""); // Attempt to remove trailing filename or slash
  if (window.location.pathname.includes("/npc")) {
    // Original hack - needs refinement maybe?
    basePath += "/npc";
  }

  const { token } = isAdmin ? getStoredAdminAuthToken() : getStoredAuthToken();
  const wsUrl = `${wsProtocol}${wsHost}/xtalk_ws/${sessionId}?t=${token}`;

  console.log("Initializing WebSocket:", wsUrl);
  
  try {
    socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      // Check if this connection was aborted
      if (connectionAbortController?.signal.aborted) {
        console.log("Connection was aborted, closing socket");
        socket?.close();
        return;
      }
      
      isConnecting = false;
      connectionAbortController = null;
      console.log("WebSocket connection established for session:", sessionId);
      handlers.onOpen();
      flushMessageQueue(); // Send any messages queued before connection opened
    };

    socket.onmessage = (event) => {
      if (event.data instanceof Blob) {
        handlers.onMessage(event.data);
      } else {
        try {
          const parsedData = JSON.parse(event.data) as WebSocketMessageData;
          handlers.onMessage(parsedData);
        } catch (error) {
          console.error(
            "Failed to parse WebSocket message:",
            error,
            "Data:",
            event.data
          );
        }
      }
    };

    socket.onclose = (event) => {
      console.log("WebSocket closed for session:", currentSessionId, "Code:", event.code);
      isConnecting = false;
      currentSessionId = null;
      connectionAbortController = null;
      //@TODO commenting as a temp fix to prevent a race condition (even flushMessageQueue is not timed correctly)
      socket = null; // Clear socket instance on close
      handlers.onClose(event);
    };

    socket.onerror = (event) => {
      console.error("WebSocket error for session:", currentSessionId, event);
      isConnecting = false;
      currentSessionId = null;
      connectionAbortController = null;
      socket = null; // Clear socket instance on error
      handlers.onError(event);
    };
  } catch (error) {
    console.error("Failed to create WebSocket:", error);
    isConnecting = false;
    currentSessionId = null;
    connectionAbortController = null;
    socket = null;
    handlers.onError(error as Event);
  }
}

export function sendWebSocketMessage(message: object | Blob) {
  const messageToSend =
    typeof message === "object" && !(message instanceof Blob)
      ? JSON.stringify(message)
      : message;

  if (socket && socket.readyState === WebSocket.OPEN) {
    const type = typeof messageToSend === "string" ? JSON.parse(messageToSend).type : "Blob";
    console.log(
      `%cSending message: ${type}`,
      "background: #fdecea; color: #7a1f1f; padding: 2px 6px; border-radius: 3px;"
    );
    socket.send(messageToSend);
  } else {
    console.warn("WebSocket not open, queuing message.");
    messageQueue.push(messageToSend);
    // Optionally: Trigger connection attempt if not already connecting
    // const { connectWebSocket, isConnecting } = useAppStore.getState();
    // if (!isConnecting) connectWebSocket();
  }
}

export function closeWebSocket() {
  console.log("Closing WebSocket...");
  
  // Abort any pending connection
  if (connectionAbortController) {
    connectionAbortController.abort();
    connectionAbortController = null;
  }
  
  // Close existing socket
  if (socket) {
    console.log("Closing existing socket for session:", currentSessionId);
    socket.close(1000, "Client initiated disconnect"); // Normal closure
    socket = null;
  }
  
  // Reset state
  isConnecting = false;
  currentSessionId = null;
  messageQueue = []; // Clear queue on explicit close
}

// Export function to check current connection state
export function getConnectionState() {
  return {
    isConnected: socket?.readyState === WebSocket.OPEN,
    isConnecting,
    currentSessionId,
    socketState: socket?.readyState
  };
}
